import React, { useState } from "react";
import { Edit, CameraAlt, Image, AttachFile } from "@mui/icons-material";
import { FilePdf } from "@phosphor-icons/react";
import { storage } from "../../common/firebaseConfig";
import { CircularProgress, Button, Snackbar, Alert } from '@mui/material';
import { useUpdateShoCoverMutation, useUpdateShopImagesMutation, useUpdateShopLogoImageMutation, useUpdateShopProfileDocumentsMutation } from "../../apis/ShopProfileApi";
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
 
const ProfileCard = ({ profileInfo, isEditMode, toggleEditMode, setValue }) => {
  const [activeTab, setActiveTab] = useState("images");
  const [updateDocuments, { isLoading, isError, error }] = useUpdateShopProfileDocumentsMutation();
  const [updateImages, { isLoading:isLoadingUpdateImage }] = useUpdateShopImagesMutation();

  const [updateCover, { isLoading:isLoadingCover }] = useUpdateShoCoverMutation();
  const [updateLogo, { isLoading:isLoadingLogo }] = useUpdateShopLogoImageMutation();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

   const [progress, setProgress] = useState(0);
     const [isUploading, setIsUploading] = useState(false);
  
  // Separate states for existing and new files/images
  const [existingDocuments, setExistingDocuments] = useState(profileInfo.documents || []);
  const [newDocuments, setNewDocuments] = useState([]);
  const [existingImages, setExistingImages] = useState(profileInfo.imagesUrls || []);
  const [newImages, setNewImages] = useState([]);
 
  const [coverImage, setCoverImage] = useState(profileInfo.coverImageUrl || "");
  const [logoImage, setLogoImage] = useState(profileInfo.imageLogoUrl || "");

  // Handle cover image change
  const handleCoverImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      convertToBase64(file).then((base64) => {
        setCoverImage(base64); // Set the Base64 string for cover image
        setValue('coverImageUrl', base64); // Update the form with the first image
        uploadImagesCover(base64, setProgress, setIsUploading)
     //   console.log('coverImageUrl',base64)
  
      });
    }
  };
  const base64ToBlob = (base64) => {
    const byteString = atob(base64.split(",")[1]);
    const mimeString = base64.split(",")[0].split(":")[1].split(";")[0];
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uint8Array = new Uint8Array(arrayBuffer);
  
    for (let i = 0; i < byteString.length; i++) {
      uint8Array[i] = byteString.charCodeAt(i);
    }
  
    return new Blob([arrayBuffer], { type: mimeString });
  };

  const uploadImagesCover = async (coverImageBase64, setProgress, setIsUploading) => {
    try {
      setIsUploading(true); // Disable submit button
  
      const coverImageUrl = coverImageBase64
        ? await handleImageUpload(coverImageBase64, `Amazonshops/coverx-${Date.now()}`, setProgress)
        : "";
      setIsUploading(false); // Enable submit button after upload completes

     await handleCoverImageUrlUpdate(coverImageUrl)
     //console.log('coverImageUrl coverImageUrl',coverImageUrl);

      return { coverImageUrl };
    } catch (error) {
      setIsUploading(false); // Enable submit button if error occurs
      throw new Error("Image upload failed: " + error.message);
    }
  };
  const uploadImagesLogo = async (logoImageBase64, setProgress, setIsUploading) => {
    try {
      setIsUploading(true); // Disable submit button
  
      // Ensure logoImageBase64 is valid before proceeding
      if (!logoImageBase64) {
        throw new Error('No image base64 data provided');
      }
  
      const logoImageUrl = logoImageBase64
        ? await handleImageUpload(logoImageBase64, `Amazonshops/logox-${Date.now()}`, setProgress)
        : "";
  
      // Ensure that the uploaded image URL is valid
      if (!logoImageUrl) {
        throw new Error('Image upload failed, received undefined or empty URL');
      }
  
      setIsUploading(false); // Enable submit button after upload completes
  
     // console.log("Uploaded logo image URL:", logoImageUrl); // Debugging log
      await handleLogoImageUrlUpdate(logoImageUrl)

      return { logoImageUrl };
    } catch (error) {
      setIsUploading(false); // Enable submit button if error occurs
      //console.error("Error during image upload:", error);
      throw new Error("Image upload failed: " + error.message);
    }
  };
  
  
  const uploadBase64ImageWithProgress = (base64Image, path, setProgress) => {
    return new Promise((resolve, reject) => {
      const blob = base64ToBlob(base64Image);
      const storageRef = ref(storage, path);
      const uploadTask = uploadBytesResumable(storageRef, blob);
  
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          // Calculate progress as a percentage
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setProgress(progress);
        },
        (error) => {
          // Handle upload errors
          reject(error);
        },
        async () => {
          // Handle successful uploads and get the download URL
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(downloadUrl);
        }
      );
    });
  };
  const isFileSizeValid = (base64) => {
    const base64Length = base64.length - (base64.indexOf(",") + 1);
    const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
    const fileSizeInBytes = (base64Length * 3) / 4 - padding;
    return fileSizeInBytes <= 2 * 1024 * 1024;
  };
  // Handle logo image change
  const uploadDocumentArrays = async (documentsBase64 = [], setProgress, setIsUploading) => {
    try {
      setIsUploading(true); // Disable submit button
  
      let totalProgress = 0;
      const updateProgress = (progress) => {
        totalProgress += progress / (documentsBase64.length);
        setProgress(totalProgress);
      };
  
      const documentsUrls = await Promise.all(
        documentsBase64.map((base64, index) =>
          handleImageUpload(base64, `AmazonshopsDocuments/documentsTest-${Date.now()}-${index}`, updateProgress)
        )
      );

      setIsUploading(false); // Enable submit button after upload completes
      return { documentsUrls};
    } catch (error) {
      setIsUploading(false); // Enable submit button if error occurs
      throw new Error("Image upload failed: " + error.message);
    }
  };

  const uploadImagesArrays = async (imagesBase64 = [], setProgress, setIsUploading) => {
    try {
      setIsUploading(true); // Disable submit button
  
      let totalProgress = 0;
      const updateProgress = (progress) => {
        totalProgress += progress / (imagesBase64.length);
        setProgress(totalProgress);
      };

      const imagesUrls = await Promise.all(
        imagesBase64.map((base64, index) =>
          handleImageUpload(base64, `AmazonshopsImages/imagesTest-${Date.now()}-${index}`, updateProgress)
        )
      );
  
      setIsUploading(false); // Enable submit button after upload completes
      return {imagesUrls};
    } catch (error) {
      setIsUploading(false); // Enable submit button if error occurs
      throw new Error("Image upload failed: " + error.message);
    }
  };

  const handleLogoImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      convertToBase64(file).then((base64) => {
        setLogoImage(base64); // Set the Base64 string for icon image

        setValue('imageLogoUrl', base64); // Update the form with the second image
        uploadImagesLogo(base64, setProgress, setIsUploading)

       // console.log('imageLogoUrl',base64)

      });
    }
  };
  // Handle PDF upload (new files only)
 // Handle file upload and convert images to base64
 async function handleNewImageUpload(event) {
  // Function to check if the file is an image
  function isValidImage(file) {
    const allowedTypes = [
      'image/jpeg',  // JPEG format
      'image/png',   // PNG format
      'image/gif',   // GIF format
      'image/webp',  // WebP format
      'image/bmp',   // BMP format
      'image/tiff',  // TIFF format
      'image/svg+xml' // SVG format
    ];
  
    const maxSize = 2 * 1024 * 1024; // Maximum size: 2MB

    if (!allowedTypes.includes(file.type)) {
      alert(`File type not allowed: ${file.name}. Please upload a valid image.`);
      return false;
    }

    if (file.size > maxSize) {
      alert(`File size exceeds the limit of 5MB: ${file.name}.`);
      return false;
    }

    return true;
  }

  // Function to read a file asynchronously
  function readFileAsync(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => resolve(reader.result);
      reader.onerror = (event) => reject(event.target.error);
      
      reader.readAsDataURL(file); // Read as Data URL (base64)
    });
  }

  const files = Array.from(event.target.files || []);
  const validFiles = files.filter(isValidImage); // Filter files by validation

  if (validFiles.length === 0) {
    alert('No valid images selected. Please try again.');
    return;
  }

  try {
    const uploadedImages = await Promise.all(validFiles.map((file) => readFileAsync(file)));

    // Update the form with valid image URLs
    setValue('imagesUrls', uploadedImages);
   // console.log('imagesUrls imagesUrls',uploadedImages)

    // Update local state with previews for valid images
    setNewImages(uploadedImages);
  } catch (error) {
    console.error("Error during file upload:", error);
    alert("An error occurred while uploading the images. Please try again.");
  }
}
async function handleNewDocumentUpload(event) {
  // Function to read the file asynchronously
  function readFileAsync(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => resolve(reader.result);

      reader.onerror = (event) => {
        console.error("File read error:", event.target.error);
        alert(`Failed to read file: ${file.name}. Please check if the file is accessible and try again.`);
        reject(event.target.error);
      };

      try {
        reader.readAsDataURL(file); // Read as Data URL (base64)
      } catch (error) {
        console.error("An unexpected error occurred while reading the file:", error);
        reject(error);
      }
    });
  }

  // Function to check file size (limit to 2MB)
  function isValidPdf(file) {
    const maxSize = 2 * 1024 * 1024; // 2MB

    if (file.size > maxSize) {
      alert(`File size exceeds the 2MB limit: ${file.name}`);
      return false;
    }

    return true;
  }

  const files = Array.from(event.target.files || []);
  
  try {
    const uploadedFiles = await Promise.all(
      files.map((file) =>
        isValidPdf(file) ? readFileAsync(file).catch((error) => {
          console.warn(`Skipping file: ${file.name} due to error:`, error);
          return null; // Skip problematic files
        }) : null // Skip file if it exceeds size limit
      )
    );

    // Filter out null values (files that failed to read or were too large)
    const validFiles = uploadedFiles.filter((file) => file !== null);

    if (validFiles.length === 0) {
      alert("No valid files were uploaded. Please try again with accessible files or files under 2MB.");
      return;
    }

    // Update the form and local state with valid files
    setValue('documents', validFiles);
   // console.log('imagesUrls imagesUrls',validFiles)

    setNewDocuments(validFiles);

  } catch (error) {
    console.error("Unexpected error during file upload:", error);
    alert("An unexpected error occurred during file upload. Please try again.");
  }
}
 
  // Handle file upload and convert images to base64
 

    // Helper function to convert file to Base64
const convertToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result); // Resolve with Base64 string
    reader.onerror = reject; // Reject in case of error
    reader.readAsDataURL(file); // Read the file as a Data URL (Base64)
  });
};


  // Delete existing document
  const handleDeleteExistingDocument = async (urlToDelete, index) => {
  try {
    // Decode the file path from the URL
    const decodedPath = decodeURIComponent(urlToDelete.split('/o/')[1].split('?')[0]);
    const fileRef = ref(storage, decodedPath);

    try {
      // 1. Attempt to delete from Firebase Storage
      await deleteObject(fileRef);
    } catch (error) {
      // If the file does not exist, log the error but continue execution
      if (error.code === 'storage/object-not-found') {
        console.warn('File does not exist, continuing with the update process:', error.message);
      } else {
        console.error('Error deleting file from Firebase:', error);
      }
    }

    // 2. Update local state
    const updatedDocuments = existingDocuments.filter((url) => url !== urlToDelete);
    setExistingDocuments(updatedDocuments);

    // 3. Update backend (always runs, even if deletion failed)
    try {
      await updateDocuments({ id: profileInfo?.id, documents: updatedDocuments }).unwrap();
      setSnackbarMessage('Document deleted and updated successfully!');
    } catch (backendError) {
      console.error('Error updating backend:', backendError);
      setSnackbarMessage('Document deletion was successful locally, but failed to update the backend.');
    }

    setSnackbarOpen(true);
  } catch (error) {
    console.error('Unexpected error deleting document:', error);
    setSnackbarMessage('Failed to delete document. Please try again.');
    setSnackbarOpen(true);
  }
};

  // Delete new document
  const handleDeleteNewDocument = (index) => {
    const updated = newDocuments.filter((_, i) => i !== index);
    setNewDocuments(updated);
  };

  // Delete existing image
  
  const handleDeleteExistingImage = async (urlToDelete, index) => {
    try {
      // Decode the file path from the URL
      const decodedPath = decodeURIComponent(urlToDelete.split('/o/')[1].split('?')[0]);
      const fileRef = ref(storage, decodedPath);
  
      try {
        // 1. Attempt to delete from Firebase Storage
        await deleteObject(fileRef);
      } catch (error) {
        // If the file does not exist, log the error but continue execution
        if (error.code === 'storage/object-not-found') {
          console.warn('File does not exist, continuing with the update process:', error.message);
        } else {
          console.error('Error deleting file from Firebase:', error);
        }
      }
  
      // 2. Update local state
      const updatedUpdate = existingImages.filter((url) => url !== urlToDelete);
      setExistingImages(updatedUpdate);
  
      // 3. Update backend (always runs, even if deletion failed)
      try {
        await updateImages({ id: profileInfo?.id, imagesUrls: updatedUpdate }).unwrap();
        setSnackbarMessage('Images deleted and updated successfully!');
      } catch (backendError) {
        console.error('Error updating backend:', backendError);
        setSnackbarMessage('Document deletion was successful locally, but failed to update the backend.');
      }
  
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Unexpected error deleting document:', error);
      setSnackbarMessage('Failed to delete document. Please try again.');
      setSnackbarOpen(true);
    }
  };

  // Delete new image
  const handleDeleteNewImage = (index) => {
    const updated = newImages.filter((_, i) => i !== index);
    setNewImages(updated);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false); 
  };
  const handleUploadNewImages = async () => {
     try {
      // 1. Upload new files to Firebase Storage (your existing logic)
      const {imagesUrls } = await uploadImagesArrays(
        newImages,setProgress, setIsUploading
      );
    // console.log("image imagesUrls sUrls",imagesUrls);         
      // 2. Update local state
      const updatedImages = [...imagesUrls, ...existingImages];
  

      // 3. Update backend
      await updateImages({ id: profileInfo?.id, imagesUrls: updatedImages }).unwrap();

      // Show success message
      setSnackbarMessage('New documents uploaded and updated successfully!');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error uploading documents:', error);
      setSnackbarMessage('Failed to upload documents. Please try again.');
      setSnackbarOpen(true);
    }
  };
  const handleNewDocuments = async () => {
    try {
      // 1. Upload new files to Firebase Storage (your existing logic)
      const {documentsUrls } = await uploadDocumentArrays(
        newDocuments,setProgress, setIsUploading
      );

      // 2. Update local state
      const updatedDocuments = [...documentsUrls, ...existingDocuments];
 
      // 3. Update backend
      await updateDocuments({ id: profileInfo?.id, documents: updatedDocuments }).unwrap();

      // Show success message
      setSnackbarMessage('New documents uploaded and updated successfully!');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error uploading documents:', error);
      setSnackbarMessage('Failed to upload documents. Please try again.');
      setSnackbarOpen(true);
    }
  };
const handleImageUpload = async (base64Image, path, setProgress) => {
  if (!isFileSizeValid(base64Image)) {
    throw new Error("File size exceeds 2 MB limit.");
  }
  return await uploadBase64ImageWithProgress(base64Image, path, setProgress);
};
  const handleLogoImageUrlUpdate = async (newLogoUrl) => {
// Decode the file path from the URL
const decodedPath = decodeURIComponent(profileInfo?.imageLogoUrl.split('/o/')[1].split('?')[0]);
const fileRef = ref(storage, decodedPath);

try {
  // 1. Attempt to delete from Firebase Storage
  await deleteObject(fileRef);
} catch (error) {
  // If the file does not exist, log the error but continue execution
  if (error.code === 'storage/object-not-found') {
    console.warn('File does not exist, continuing with the update process:', error.message);
  } else {
    console.error('Error deleting file from Firebase:', error);
  }
}
try {
const response = await updateLogo({ shopProfileId: profileInfo?.id, imageLogoUrl: newLogoUrl }).unwrap();
// console.log('Logo updated successfully', response);
} catch (error) {
console.error('Failed to update logo', error);
}
   
}
const handleCoverImageUrlUpdate = async (coverImageUrl) => {
  // Decode the file path from the URL
  const decodedPath = decodeURIComponent(profileInfo?.coverImageUrl.split('/o/')[1].split('?')[0]);
  const fileRef = ref(storage, decodedPath);

  try {
    // 1. Attempt to delete from Firebase Storage
    await deleteObject(fileRef);
  } catch (error) {
    // If the file does not exist, log the error but continue execution
    if (error.code === 'storage/object-not-found') {
      console.warn('File does not exist, continuing with the update process:', error.message);
    } else {
      console.error('Error deleting file from Firebase:', error);
    }
  }
  try {
    const response = await updateCover({ shopProfileId: profileInfo?.id, coverImageUrl: coverImageUrl }).unwrap();
   // console.log('Logo updated successfully', response);
  } catch (error) {
    console.error('Failed to update logo', error);
  }
}
  return (
    <div className="container mt-5">
      <div className="card position-relative">
        {/* Cover Image */}
        <div className="position-relative">
          <img
            src={coverImage || "default-cover.jpg"}
            className="card-img-top"
            alt="Cover"
            style={{ height: "300px", objectFit: "cover", width: "100%" }}
          />
          {isEditMode && (
          <div className="position-absolute top-0 end-0 p-3" style={{ zIndex: 10 }}>
          <input
            type="file"
            id="coverImageUpload"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleCoverImageChange}
          />
          <label 
            htmlFor="coverImageUpload" 
            className="btn btn-main py-3 px-8 rounded-8 rounded-circle d-flex align-items-center justify-content-center"
            style={{ width: "40px", height: "40px", position: "relative" }}
          >
            {isLoadingCover ? (
              <div className="spinner-border text-light" role="status" style={{ width: "20px", height: "20px" }}></div>
            ) : (
              <Edit />
            )}
          </label>
        </div>
        
          )}
        </div>

        {/* Profile Info Section */}
        <div className="card-body pt-5 mt-5">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center align-items-md-end mb-4">
            {/* Profile Picture & Details */}
            <div className="d-flex flex-column flex-md-row align-items-center">
              <div className="position-relative">
                <img
                  src={logoImage || "default-avatar.jpg"}
                  className="rounded-circle shadow"
                  alt="Profile"
                  style={{
                    width: "150px",
                    height: "150px",
                    objectFit: "cover",
                    border: "5px solid white",
                    marginTop: "-100px",
                  }}
                />
                {isEditMode && (
                  <div className="position-absolute bottom-0 end-0 p-2" style={{ zIndex: 10 }}>
                    <input
                      type="file"
                      id="imageLogoUrl"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleLogoImageChange}
                    />
                  
                    <label htmlFor="imageLogoUrl" className="btn btn-main py-3 px-8 rounded-8 rounded-circle">

            {isLoadingLogo ? (
              <div className="spinner-border text-light" role="status" style={{ width: "20px", height: "20px" }}></div>
            ) : (
              <CameraAlt />
            )}
          </label>
                  </div>
                )}
              </div>

              <div className="mt-3 mt-md-0 ms-md-4 text-center text-md-start">
                <h1 className="mb-0">{profileInfo.name}</h1>
                <p className="lead text-muted">
                  {profileInfo.categoryProducts && profileInfo.categoryProducts.length > 0
                    ? profileInfo.categoryProducts.map((category, index) => (
                        <span key={category.id}>
                          {category.name}
                          {index < profileInfo.categoryProducts.length - 1 ? ", " : ""}
                        </span>
                      ))
                    : "No categories selected"}
                </p>
                <div className="text-muted">{profileInfo.shopDescription}</div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="d-flex gap-2 mt-3 mt-md-0">
              <button className="btn btn-main py-9 px-8 rounded-8" onClick={toggleEditMode}>
                {isEditMode ? "Cancel" : "Edit Profile"}
              </button>
            </div>
          </div>

          {/* Tabbed Interface for Image and File Updates */}
          {isEditMode && (
            <div className="mt-4">
              <ul className="nav nav-tabs">
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "images" ? "active" : ""}`}
                    onClick={() => setActiveTab("images")}
                  >
                    <Image fontSize="small" className="me-2" />
                    Update Images
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "files" ? "active" : ""}`}
                    onClick={() => setActiveTab("files")}
                  >
                    <AttachFile fontSize="small" className="me-2" />
                    Upload Files
                  </button>
                </li>
              </ul>

              <div className="tab-content mt-3">
                {activeTab === "images" && (
                  <div className="tab-pane fade show active">
                    <div className="mb-3">
                      {/* Existing Images */}
                      <h6>Existing Images</h6>
                      <div className="row">
                        {existingImages.map((image, index) => (
                          <div key={`existing-image-${index}`} className="col-sm-4 mb-3 position-relative">
                            <img
                              src={image}
                              alt={`Existing ${index + 1}`}
                              className="img-fluid rounded-8"
                              style={{ maxHeight: "150px", objectFit: "cover" }}
                            />
                            <button
                              className="btn btn-danger btn-sm position-absolute top-0 end-0"
                              onClick={() => handleDeleteExistingImage(image,index)}
                            >
                              ×
                            </button>
                             {/* Loading indicator */}
                            
                          </div>
                        ))}
                         {isLoadingUpdateImage && (
                                  <div style={{ marginTop: '16px' }}>
                                    <CircularProgress />
                                    <span style={{ marginLeft: '8px' }}>Updating ...</span>
                                  </div>
                                )}
                      </div>

                      {/* New Images */}
                      <h6>New Images</h6>
                      <div className="row">
                        {newImages.map((image, index) => (
                          <div key={`new-image-${index}`} className="col-sm-4 mb-3 position-relative">
                            <img
                              src={image}
                              alt={`New ${index + 1}`}
                              className="img-fluid rounded-8"
                              style={{ maxHeight: "150px", objectFit: "cover" }}
                            />
                            <button
                              className="btn btn-danger btn-sm position-absolute top-0 end-0"
                              onClick={() => handleDeleteNewImage(index)}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                      <input
                        type="file"
                        className="common-input px-16"
                        multiple
                        accept="image/*"
                        onChange={handleNewImageUpload}
                      />
                    </div> 

                    <button
      type="submit"
      className="btn btn-main py-18 px-32 rounded-8"
      disabled={isLoadingUpdateImage || isUploading} // Disable button while either uploading or loading
      onClick={handleUploadNewImages}
    >
      {isUploading ? `Uploading... ${progress}%` : isLoadingUpdateImage ? 'Submitting...' : 'updatee'}
    </button>

    {/* Show progress bar for image upload */}
    {isUploading && (
      <div className="mt-4">
        <progress value={progress} max="100" className="w-full"></progress>
      </div>
    )}
              </div>
                )}

                {activeTab === "files" && (
                  <div className="tab-pane fade show active">
                    <div className="mb-3">
                          {/* Display Existing Documents */}
                      {existingDocuments.map((document, index) => (
                        <div
                          key={index}
                          className="position-relative mb-3"
                        >
                          <a
                            href={document}
                            className="d-block text-muted"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View Existing File {index + 1}
                          </a>
                          <button
                            className="btn btn-danger position-absolute top-0 end-0 p-2"
                            onClick={() => handleDeleteExistingDocument(document, index)}
                            style={{ zIndex: 10 }}
                          >
                            <span>&times;</span>
                          </button>

                          
                                
                               
                        </div>
                      ))}
                      {/* Loading indicator */}
                      {isLoading && (
                                  <div style={{ marginTop: '16px' }}>
                                    <CircularProgress />
                                    <span style={{ marginLeft: '8px' }}>Updating documents...</span>
                                  </div>
                                )}
                          
                      {/* Existing Documents */}
                        

                      {/* New Documents */}
                      <h6>New Documents</h6>
                      <div className="row">
                        {newDocuments.map((doc, index) => (
                          <div key={`new-doc-${index}`} className="col-sm-4 mb-3 position-relative">
                            <FilePdf size={50} color="blue" />
                            <button
                              className="btn btn-danger btn-sm position-absolute top-0 end-0"
                              onClick={() => handleDeleteNewDocument(index)}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                      <input
                        type="file"
                        className="common-input px-16"
                        multiple
                        accept="application/pdf"
                        onChange={handleNewDocumentUpload}
                      />

<button
      type="submit"
      className="btn btn-main py-18 px-32 rounded-8"
      disabled={isLoading || isUploading} // Disable button while either uploading or loading
      onClick={handleNewDocuments}
    >
      {isUploading ? `Uploading... ${progress}%` : isLoading? 'Submitting...' : 'Update'}
    </button>

    {/* Show progress bar for image upload */}
    {isUploading && (
      <div className="mt-4">
        <progress value={progress} max="100" className="w-full"></progress>
      </div>
    )}
                    </div>
                     {/* Snackbar for success/error messages */}
                     <Snackbar
                                  open={snackbarOpen}
                                  autoHideDuration={6000}
                                  onClose={handleCloseSnackbar}
                                  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                                >
                                  <Alert
                                    onClose={handleCloseSnackbar}
                                    severity={isError ? 'error' : 'success'}
                                    sx={{ width: '100%' }}
                                  >
                                    {snackbarMessage}
                                  </Alert>
                                </Snackbar>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileCard;